import AuthenticationSource from 'common/src/vtj/AuthenticationSource.enum'
import {
  EidasOrderer,
  HetuOrderer,
  Orderer,
} from 'todistukset-common/src/vtj/types/todistus'
import {
  EidasIdentifiedUser,
  HetuIdentifiedUser,
  TodistuksetUser,
} from 'todistukset-common/src/vtj/types/user'
import {
  HetuTodistusPerson,
  TodistusAsiointikieli,
  TodistusPerson,
  TodistusPersonHetuSource,
  TodistusPersonRole,
  TodistusPersonType,
  UnpersistedTodistusPerson,
} from 'todistukset-common/src/vtj/types/todistus-person.type'
import { Huollettava } from 'todistukset-common/src/vtj/vtj-kysely/huoltod-soap-response-converter'
import { differenceInYears, startOfDay, startOfToday } from 'date-fns'
import {
  editIlmoitettuArvo,
  editVtjArvo,
  Etunimet,
  Sukunimi,
  TilaustenMaara,
} from 'todistukset-common/src/vtj/types/editable-field.type'

export const getHetu = (user: UserLike): string | undefined =>
  'hetu' in user && user.hetu !== null ? user.hetu : undefined
export const getEidasPID = (user: UserLike): string | undefined =>
  'eidasPID' in user && user.eidasPID !== null ? user.eidasPID : undefined
export const getYTunnus = (user: UserLike): string | undefined =>
  'yTunnus' in user && user.yTunnus !== null ? user.yTunnus : undefined

export const isLoggedIn = (user: UserLike | undefined): boolean => {
  if (!user) {
    return false
  }
  return !!getHetu(user) || !!getEidasPID(user)
}

type UserLike = Readonly<
  | { eidasPID?: string | null }
  | { hetu?: string | null }
  | { yTunnus?: string | null }
>

export const isSameHetuOrEidasPID = (
  user1: UserLike | undefined,
  user2: UserLike | undefined
): boolean => {
  if (!user1 || !user2) {
    return false
  }
  const hetu1 = getHetu(user1)
  if (!!hetu1 && hetu1 === getHetu(user2)) {
    return true
  }

  const eidasPID1 = getEidasPID(user1)
  if (!!eidasPID1 && eidasPID1 === getEidasPID(user2)) {
    return true
  }

  const yTunnus1 = getYTunnus(user1)
  if (!!yTunnus1 && yTunnus1 === getYTunnus(user2)) {
    return true
  }

  return false
}

export const isEidasIdentifiedUser = (
  user: Readonly<TodistuksetUser>
): user is EidasIdentifiedUser =>
  user.authenticationSource === AuthenticationSource.EIDAS

export const isHetuIdentifiedUser = (
  user: Readonly<TodistuksetUser>
): user is HetuIdentifiedUser =>
  user.authenticationSource === AuthenticationSource.FINNISH

export const getUniqueIdentifier = (user: UserLike): string => {
  const hetu = getHetu(user)
  if (hetu) {
    return `hetu|${hetu}`
  }
  const eidasPID = getEidasPID(user)
  if (eidasPID) {
    return `eidasPID|${eidasPID}`
  }
  throw new Error(
    'UserLike contains neither hetu nor eidasPID which could be used for unique identifier'
  )
}

export function getOrdererFromUser(user: HetuIdentifiedUser): HetuOrderer
export function getOrdererFromUser(user: EidasIdentifiedUser): EidasOrderer
export function getOrdererFromUser(
  user: TodistuksetUser
): HetuOrderer | EidasOrderer
export function getOrdererFromUser(
  user: TodistuksetUser
): HetuOrderer | EidasOrderer {
  const nimiEditFn = isEidasIdentifiedUser(user)
    ? editIlmoitettuArvo
    : editVtjArvo
  const common = {
    firstNames: nimiEditFn<Etunimet>(null, user.firstNames),
    lastName: nimiEditFn<Sukunimi>(null, user.lastName),
  }
  if (isHetuIdentifiedUser(user)) {
    return {
      ...common,
      authenticationSource: user.authenticationSource,
      hetu: user.hetu,
      kotikuntaKoodi: user.kotikuntaKoodi,
    } satisfies HetuOrderer
  } else if (isEidasIdentifiedUser(user)) {
    return {
      ...common,
      authenticationSource: user.authenticationSource,
      eidasPID: user.eidasPID,
      eidasBirthdate: user.birthday,
      kotikuntaKoodi: user.kotikuntaKoodi,
    } satisfies EidasOrderer
  }
  throw new Error('Unhandled user type')
}

export const isEidasOrderer = (orderer: Orderer): orderer is EidasOrderer => {
  return orderer.authenticationSource === AuthenticationSource.EIDAS
}

export const isItsepalveluOrderer = (
  orderer: Orderer
): orderer is HetuOrderer | EidasOrderer => {
  return [AuthenticationSource.FINNISH, AuthenticationSource.EIDAS].includes(
    orderer.authenticationSource
  )
}

export const isHetuPerson = (
  person: TodistusPerson
): person is HetuTodistusPerson => {
  return 'hetu' in person && !!person.hetu
}

function hasTurned({
  years,
  birthday,
}: {
  years: number
  /** yyyy-MM-dd */
  birthday: string
}): boolean {
  const startOfBirthdayDate = startOfDay(new Date(birthday))
  return differenceInYears(startOfToday(), startOfBirthdayDate) >= years
}

/** @param birthday in date-fns format yyyy-MM-dd */
export function determineIsOver15(birthday: string): boolean {
  return hasTurned({ years: 15, birthday })
}

/** @param birthday in date-fns format yyyy-MM-dd */
export function determineIsAdult(birthday: string): boolean {
  return hasTurned({ years: 18, birthday })
}

export function getPersonFromUser(
  user: TodistuksetUser,
  {
    isSubject = false,
    asiointikieli,
  }: { isSubject?: boolean; asiointikieli: TodistusAsiointikieli }
): UnpersistedTodistusPerson {
  const nimiEditFn = isEidasIdentifiedUser(user)
    ? editIlmoitettuArvo
    : editVtjArvo
  const person = {
    personType: TodistusPersonType.IHMINEN,
    firstNames: nimiEditFn<Etunimet>(null, user.firstNames),
    lastName: nimiEditFn<Sukunimi>(null, user.lastName),
    isDependant: false,
    kotikuntaKoodi: user.kotikuntaKoodi,
    roles: isSubject
      ? [TodistusPersonRole.TILAAJA, TodistusPersonRole.KOHDEHENKILO]
      : [TodistusPersonRole.TILAAJA],
    phoneNumber: null,
    birthdate: null, // @TODO get real birthdate?
    hetu: isHetuIdentifiedUser(user) ? user.hetu : null,
    hetuSource: TodistusPersonHetuSource.TUNNISTUS,
    citizenshipCode: null,
    email: null,
    religiousCommunityTranslated: [],
    countryOfResidenceCode: null,
    maritalStatus: null,
    numberOfMarriages: null,
    orderedAmount: editIlmoitettuArvo<TilaustenMaara>(null, 1),
    eidasPID: isEidasIdentifiedUser(user) ? user.eidasPID : null,
    birthPlace: null,
    birthCountryCode: null,
    asiointikieli,
    yTunnus: null,
    organizationName: null,
  } satisfies UnpersistedTodistusPerson
  return person
}

export function huollettavaToPerson(
  huollettava: Huollettava,
  { isSubject = true }: { isSubject?: boolean } = {}
): UnpersistedTodistusPerson {
  return {
    personType: TodistusPersonType.IHMINEN,
    hetu: huollettava.hetu,
    kotikuntaKoodi: null,
    roles: isSubject
      ? [TodistusPersonRole.HUOLLETTAVA, TodistusPersonRole.KOHDEHENKILO]
      : [TodistusPersonRole.HUOLLETTAVA],
    phoneNumber: null,
    birthdate: null,
    citizenshipCode: null,
    email: null,
    hetuSource: null,
    religiousCommunityTranslated: [],
    maritalStatus: null,
    countryOfResidenceCode: null,
    numberOfMarriages: null,
    eidasPID: null,
    orderedAmount: editIlmoitettuArvo<TilaustenMaara>(null, 1),
    isDependant: true,
    firstNames: editVtjArvo<Etunimet>(null, huollettava.firstNames),
    lastName: editVtjArvo<Sukunimi>(null, huollettava.lastName),
    birthPlace: null,
    birthCountryCode: null,
    asiointikieli: null,
    yTunnus: null,
    organizationName: null,
  }
}
